import { Null } from "@/components/null";
import { mergeAttributes, Node, type NodeViewProps } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { lazy, Suspense, useEffect } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import type { muxVideoExtensionQuery } from "./__generated__/muxVideoExtensionQuery.graphql";
import type { muxVideoQuery } from "./__generated__/muxVideoQuery.graphql";
import type { muxVideoExtensionFragment$key } from "./__generated__/muxVideoExtensionFragment.graphql";
import { Spinner } from "@/components/ui/spinner";

import { toGlobalId } from "graphql-relay";
import { useColors } from "@/hooks/useColors";

const MuxPlayer = lazy(() => import("@/components/lazy-mux-player"));

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		"mux-video": {
			/**
			 * Set media
			 */
			setMuxVideo: (options: { assetId: string; fileId: string }) => ReturnType;
		};
	}
}

function Player({ id, assetId }: { id: string; assetId: string }) {
	const { primary, accent, secondary } = useColors();
	const data = useLazyLoadQuery<muxVideoExtensionQuery>(
		graphql`
    query muxVideoExtensionQuery($id: ID!) {
      node(id: $id) {
        ... on VideoFile {
          id
          ...muxVideoExtensionFragment
        }
      }
    }
  `,
		{ id },
	);
	const [video, refetch] = useRefetchableFragment<
		muxVideoQuery,
		muxVideoExtensionFragment$key
	>(
		graphql`
    fragment muxVideoExtensionFragment on VideoFile
    @refetchable(queryName: "muxVideoQuery")
    {
      id
      status
      thumbnail {
        signedUrl
      }
      playback {
        playback
        tokens {
          thumbnail
          playback
          storyboard
        }
      }
    }
  `,
		data.node,
	);

	useEffect(() => {
		if (video?.id && video?.status === "PROCESSING") {
			const timeout = setTimeout(() => {
				refetch({}, { fetchPolicy: "network-only" });
			}, 5000);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [video?.status, refetch, video?.id]);

	if (video?.status === "PROCESSING") {
		return (
			<div
				className="flex w-full animate-pulse flex-col items-center justify-center gap-3 rounded-md border"
				style={{ aspectRatio: 16 / 9 }}
			>
				<Spinner />

				<span className="font-medium text-muted-foreground text-sm">
					Vídeo em processamento
				</span>
			</div>
		);
	}

	if (!video?.playback) {
		return null;
	}

	return (
		<>
			<style>
				{`
      		mux-player {
            width: 100%;
            max-width: 900px;
          }
        `}
			</style>
			<MuxPlayer
				playbackId={video.playback?.playback}
				tokens={{
					thumbnail: video.playback?.tokens?.thumbnail || undefined,
					playback: video.playback?.tokens?.playback || undefined,
					storyboard: video.playback?.tokens?.storyboard || undefined,
				}}
				streamType="on-demand"
				{...(video?.thumbnail?.signedUrl
					? { poster: video.thumbnail.signedUrl }
					: {})}
				metadata={{
					video_id: assetId,
				}}
				style={{ aspectRatio: 16 / 9 }}
				accentColor={primary}
				secondaryColor="transparent"
			/>
		</>
	);
}

function MuxVideo({ node }: NodeViewProps) {
	if (!node.attrs.fileId || !node.attrs.assetId) {
		return null;
	}

	return (
		<NodeViewWrapper className="mux-video">
			<Suspense fallback={<Null />}>
				<Player
					id={toGlobalId("VideoFile", node.attrs.fileId)}
					assetId={node.attrs.assetId}
				/>
			</Suspense>
		</NodeViewWrapper>
	);
}

export const VideoExtension = Node.create({
	name: "mux-video",
	group: "block",
	atom: true,

	addAttributes() {
		return {
			fileId: {
				isRequired: true,
			},
			assetId: {
				isRequired: true,
			},
		};
	},

	addNodeView() {
		return ReactNodeViewRenderer(MuxVideo);
	},

	addCommands() {
		return {
			setMuxVideo:
				(options: { fileId: string; assetId: string }) =>
				({ commands }) => {
					return commands.insertContent([
						{
							type: this.name,
							attrs: options,
						},
						{
							type: "hardBreak",
						},
					]);
				},
		};
	},

	parseHTML() {
		return [
			{
				tag: "mux-video",
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		return ["mux-video", mergeAttributes(HTMLAttributes)];
	},
});
